// src/store/useAuthStore.ts
import { AuthenticationFactory } from "service/authentication/authenticationFactory";
import { AuthProvider } from "service/authentication/authenticationTypes";
import { create } from "zustand";
import { persist } from "zustand/middleware";

export interface AuthTokens {
    AccessToken?: string;
    RefreshToken?: string;
    IdToken?: string;
}

interface AuthState {
    // State
    tokens: AuthTokens | null;
    isAuthenticated: boolean;
    isInitializing: boolean;
    userEmail: string | null;

    // Actions
    setTokens: (tokens: AuthTokens | null) => void;
    clearTokens: () => void;
    getCurrentProvider: () => AuthProvider;
    setInitializing: (isInitializing: boolean) => void;
    setIsAuthenticated: (isAuthenticated: boolean) => void;
}

export const useAuthStore = create<AuthState>()(
    persist(
        (set, get) => ({
            // Initial state
            tokens: null,
            isAuthenticated: false,
            isInitializing: true,
            userEmail: null,

            // Set tokens and update auth status
            // Zustand persist() handles localStorage automatically
            setTokens: (tokens) => {
                set({
                    tokens,
                    isAuthenticated: !!tokens?.AccessToken,
                });
            },

            // Clear all auth data
            clearTokens: () => {
                set({
                    tokens: null,
                    isAuthenticated: false,
                    userEmail: null,
                });
            },

            // Get current auth provider
            getCurrentProvider: () => {
                return AuthenticationFactory.getCurrentProvider();
            },

            setInitializing: (isInitializing: boolean) => {
                set({ isInitializing });
            },

            setIsAuthenticated: (isAuthenticated: boolean) => {
                set({ isAuthenticated });
            },
        }),
        {
            name: "tokens",
            // Only persist tokens, not the entire state
            partialize: (state) => ({
                tokens: state.tokens,
            }),
            onRehydrateStorage: () => {
                return (state, error) => {
                    if (error) {
                        console.error("Rehydration error:", error);
                    } else if (state) {
                        // Recalculate isAuthenticated from restored tokens
                        state.isAuthenticated = !!state.tokens?.AccessToken;
                    }
                };
            },
        }
    )
);
